import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';
import api from '@/lib/api';

const SEVERITY_COLORS = { CRITICAL: '#FF3B30', HIGH: '#FF9500', MEDIUM: '#FFCC02', LOW: '#607D8B' };

const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.getNotifications();
      setItems(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch { /* silent — bell is non-critical */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const markAllRead = async () => {
    try {
      await api.markNotificationsRead({ all: true });
      setUnread(0);
      setItems(prev => prev.map(n => ({ ...n, read: true })));
    } catch { /* silent */ }
  };

  const openItem = (n) => {
    setOpen(false);
    if (!n.read) {
      api.markNotificationsRead({ ids: [n.id] }).catch(() => {});
      setItems(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
      setUnread(u => Math.max(0, u - 1));
    }
    if (n.link) navigate(n.link);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="relative text-[#B0BEC5] hover:text-[#4DD0E1] transition-colors duration-200 p-2"
        data-testid="notification-bell-btn"
        aria-label={`Notifications, ${unread} unread`}
        title="Notifications"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span
            className="absolute top-0 right-0 min-w-[16px] h-4 px-1 rounded-full bg-[#FF3B30] text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="notification-badge"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-96 max-w-[90vw] bg-white rounded-lg shadow-xl border border-[#E0E0E0] z-50 overflow-hidden"
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E0E0E0]">
            <span className="text-sm font-bold text-[#0D3B4F]">Notifications</span>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                className="flex items-center gap-1 text-xs text-[#0097A7] hover:text-[#00707C] font-medium"
                data-testid="mark-all-read-btn"
              >
                <CheckCheck className="h-3.5 w-3.5" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="py-10 text-center text-sm text-[#4E6974]" data-testid="notification-empty">
                No notifications yet
              </div>
            ) : (
              items.map((n, i) => (
                <button
                  key={n.id}
                  onClick={() => openItem(n)}
                  className={`w-full text-left px-4 py-3 border-b border-[#F0F0F0] hover:bg-[#F5F5F5] transition-colors ${n.read ? 'opacity-70' : 'bg-[#F0FAFB]'}`}
                  data-testid={`notification-item-${i}`}
                >
                  <div className="flex items-start gap-2.5">
                    <span className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: SEVERITY_COLORS[n.severity] || '#607D8B' }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#0D3B4F] truncate">{n.title}</p>
                      <p className="text-xs text-[#4E6974] mt-0.5 line-clamp-2">{n.body}</p>
                      <p className="text-[10px] text-[#90A4AE] mt-1">{timeAgo(n.created_at)}</p>
                    </div>
                    {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#0097A7] mt-2 flex-shrink-0" />}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
